import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, Check } from "lucide-react";
import "./styles.css";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  containerClassName?: string;
  error?: string;
  dropdownPosition?: "top" | "bottom";
}

const Select: React.FC<SelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = "Select an option",
  className = "",
  containerClassName = "",
  error,
  dropdownPosition = "bottom",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find((option) => option.value === value);
  const displayValue = selectedOption ? selectedOption.label : placeholder;

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className={`select-container ${containerClassName} ${error ? "select-container--error" : ""}`}>
      {label && <label className="select-label">{label}</label>}
      <div className="select-wrapper" ref={selectRef}>
        <button
          type="button"
          className={`select-button ${isOpen ? "open" : ""} ${value ? "selected" : ""} ${className}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>{displayValue}</span>
          <ChevronDown size={16} className={`select-chevron ${isOpen ? "open" : ""}`} />
        </button>
        {isOpen && (
          <div className={`select-menu ${dropdownPosition === "top" ? "select-menu--top" : ""}`}>
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`select-menu-item ${value === option.value ? "selected" : ""}`}
                onClick={() => handleSelect(option.value)}
              >
                <span>{option.label}</span>
                {value === option.value && <Check size={16} />}
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <span className="select-error">{error}</span>}
    </div>
  );
};

export default Select;

